import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { queryTestRuns } from '@/features/executions/data';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import { buildTestRunPath } from '@/features/shared/components/tableSystem/tableRoutes';
import { TableV2DurationCell } from '@/features/shared/components/tableSystem/TableV2DurationCell';
import { TableV2NestedEmptyState } from '@/features/shared/components/tableSystem/TableV2NestedEmptyState';
import { TableV2NestedFrame } from '@/features/shared/components/tableSystem/TableV2NestedFrame';
import {
  buildTableV2NestedBodyRowSx,
  queryAllTableV2NestedRows,
  tableV2NestedContentSx,
  tableV2NestedDepthSx,
} from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2ActionsHeaderIcon,
  TableV2DateTimeCell,
  TableV2HeaderLabel,
  TableV2NameCell,
  TableV2NestedCollapse,
  TableV2SortableHeader,
  TableV2StatusChip,
  TableV2TagGroup,
  tableV2ActionsColumnWidth,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { formatTableV2RunNumber } from '@/features/shared/components/tableSystem/TableV2RunFormatting';
import {
  TableV2SkeletonRow,
  type TableV2SkeletonColumnKind,
} from '@/features/shared/components/tableSystem/TableV2Skeleton';
import { resolveTableV2NestedSkeletonRowCount } from '@/features/shared/components/tableSystem/TableV2SkeletonRows';
import { buildNestedTestRunFilters } from '@/features/tests/components/TestsTableV2.helpers';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { s } from '@/theme/tokens';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@/ui/primitives';
import type { TagColor } from '@/shared/data/tags';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult, TestRunDto } from '@flowqa/shared';

type SortableNestedTestRunField = 'durationMs' | 'runNumber' | 'status' | 'timestamp';

const defaultNestedTestRunSort = { field: 'timestamp', direction: 'desc' } satisfies ApiSort;

const testRunSkeletonColumns = [
  'name',
  'status',
  'duration',
  'dateTime',
  'tags',
  'actions',
] satisfies TableV2SkeletonColumnKind[];

const testRunColgroup = (
  <colgroup>
    <col width="24%" />
    <col width="11%" />
    <col width="13%" />
    <col width="24%" />
    <col width="22%" />
    <col width={tableV2ActionsColumnWidth} />
  </colgroup>
);

const buildTestRunHeader = ({
  sort,
  onSortChange,
}: {
  sort: ApiSort;
  onSortChange: (field: SortableNestedTestRunField) => void;
}): ReactElement => (
  <TableRow>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        field="runNumber"
        label="Run"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        field="status"
        label="Status"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        field="durationMs"
        label="Duration"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        field="timestamp"
        label="Ran At"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2HeaderLabel label="Tags" />
    </TableCell>
    <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
      <TableV2ActionsHeaderIcon />
    </TableCell>
  </TableRow>
);

const TestRunNestedRow = ({
  deleteRowPendingId,
  row,
  tagColorByName,
  updateRowTagsColorPendingId,
  onDeleteRow,
  onEditRow,
  onUpdateRowTagsColor,
}: {
  deleteRowPendingId: string | null;
  row: TestRunDto;
  tagColorByName: Partial<Record<string, TagColor>>;
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: TestRunDto) => void;
  onEditRow: (row: TestRunDto) => void;
  onUpdateRowTagsColor: (input: { row: TestRunDto; tags: string[]; colors: TagColor[] }) => void;
}): ReactElement => {
  const testRunNumberLabel = formatTableV2RunNumber(row.runNumber) ?? '-';
  const testRunHref = buildTestRunPath(row.testSlug ?? row.testId, row.runNumber);
  const rowName = row.displayName ?? row.testName ?? row.name ?? row.id;

  return (
    <TableRow sx={buildTableV2NestedBodyRowSx({ depth: 1, hasRenderedDivider: true })}>
      <TableCell>
        <TableV2NameCell href={testRunHref} primary={testRunNumberLabel} />
      </TableCell>
      <TableCell>
        <TableV2StatusChip status={row.status} />
      </TableCell>
      <TableCell>
        <TableV2DurationCell value={row.durationMs} />
      </TableCell>
      <TableCell>
        <TableV2DateTimeCell value={row.timestamp} />
      </TableCell>
      <TableCell>
        <TableV2TagGroup tagColorByName={tagColorByName} tags={row.tags ?? []} />
      </TableCell>
      <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
        <TableActionsCell
          rowName={rowName}
          verticalAlign="center"
          deleteActionLabel={getEntityActionLabel('delete', 'Test Run')}
          editActionLabel={getEntityActionLabel('edit', 'Test Run')}
          editTagsEntityNoun="test run"
          rowTags={row.tags ?? []}
          onEdit={() => {
            onEditRow(row);
          }}
          onUpdateTagsColor={(input) => {
            onUpdateRowTagsColor({ row, ...input });
          }}
          updateTagsColorLoading={updateRowTagsColorPendingId === row.id}
          onDelete={() => {
            onDeleteRow(row);
          }}
          disableDelete={deleteRowPendingId === row.id}
        />
      </TableCell>
    </TableRow>
  );
};

export const TestExpandedRuns = ({
  childCount,
  deleteRowPendingId,
  expanded,
  organisationId,
  projectId,
  testId,
  latestOnly,
  latestRunId,
  tagColorByName,
  testRunsScopeKey,
  testRunsQueryFn,
  updateRowTagsColorPendingId,
  onDeleteRunRow,
  onEditRunRow,
  onUpdateRunRowTagsColor,
}: {
  childCount: number;
  deleteRowPendingId: string | null;
  expanded: boolean;
  organisationId: string | null;
  projectId: string;
  testId: string;
  latestOnly: boolean;
  latestRunId: string | null;
  tagColorByName: Partial<Record<string, TagColor>>;
  testRunsScopeKey?: string;
  testRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  updateRowTagsColorPendingId: string | null;
  onDeleteRunRow: (row: TestRunDto) => void;
  onEditRunRow: (row: TestRunDto) => void;
  onUpdateRunRowTagsColor: (input: { row: TestRunDto; tags: string[]; colors: TagColor[] }) => void;
}): ReactElement => {
  const [sort, setSort] = useState<ApiSort>(defaultNestedTestRunSort);
  const handleSortChange = (field: SortableNestedTestRunField): void => {
    setSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };
  const header = buildTestRunHeader({ sort, onSortChange: handleSortChange });
  const query = useQuery({
    queryKey: queryKeys.dataGrid(
      gridPaths.testRuns,
      'v2-nested',
      organisationId,
      projectId,
      testId,
      latestOnly ? (latestRunId ?? 'missing-latest-run') : 'all-runs',
      testRunsScopeKey ?? 'default',
      sort.field,
      sort.direction,
    ),
    enabled: expanded,
    queryFn: async () => {
      const input = {
        page: 1,
        pageSize: 1,
        sorts: [sort],
        filters: buildNestedTestRunFilters({ latestOnly, latestRunId, testId }),
      };
      const queryPage =
        latestOnly && latestRunId !== null
          ? async (nextInput: GridQueryInput) =>
              await queryTestRuns(projectId, organisationId, nextInput)
          : (testRunsQueryFn ??
            (async (nextInput: GridQueryInput) =>
              await queryTestRuns(projectId, organisationId, nextInput)));
      return await queryAllTableV2NestedRows(input, queryPage);
    },
    staleTime: 30_000,
  });
  const skeletonRowCount = resolveTableV2NestedSkeletonRowCount(childCount);

  return (
    <TableV2NestedCollapse expanded={expanded}>
      <Box sx={tableV2NestedContentSx}>
        {query.isLoading ? (
          <TableV2NestedFrame depth={1}>
            <Table aria-label="Test runs loading" sx={tableV2NestedDepthSx[1].table}>
              {testRunColgroup}
              <TableHead>{header}</TableHead>
              <TableBody>
                {Array.from({ length: skeletonRowCount }, (_item, index) => (
                  <TableV2SkeletonRow
                    key={`test-runs-nested-skeleton-${String(index)}`}
                    columns={testRunSkeletonColumns}
                    isLastVisibleRow={index === skeletonRowCount - 1}
                    rowIndex={index}
                  />
                ))}
              </TableBody>
            </Table>
          </TableV2NestedFrame>
        ) : query.data && query.data.items.length > 0 ? (
          <TableV2NestedFrame depth={1}>
            <Table aria-label="Test runs" sx={tableV2NestedDepthSx[1].table}>
              {testRunColgroup}
              <TableHead>{header}</TableHead>
              <TableBody>
                {query.data.items.map((testRun) => (
                  <TestRunNestedRow
                    key={testRun.id}
                    deleteRowPendingId={deleteRowPendingId}
                    row={testRun}
                    tagColorByName={tagColorByName}
                    updateRowTagsColorPendingId={updateRowTagsColorPendingId}
                    onDeleteRow={onDeleteRunRow}
                    onEditRow={onEditRunRow}
                    onUpdateRowTagsColor={onUpdateRunRowTagsColor}
                  />
                ))}
              </TableBody>
            </Table>
          </TableV2NestedFrame>
        ) : (
          <TableV2NestedEmptyState
            ariaLabel="Test runs"
            colgroup={testRunColgroup}
            colSpan={6}
            depth={1}
            header={header}
          />
        )}
      </Box>
    </TableV2NestedCollapse>
  );
};
